import { getBEFinSession, loginToBEFin } from 'api/v2/users/beFinances';
import { INTERVAT_GRANT_SOURCE } from 'components/pages/Taxes/IntervatGrantModal';
import { useEffect, useMemo, useRef } from 'react';
import useQuery from 'hooks/react-query/useQuery';

import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useGrantIntervatAccessToWorkspace from 'hooks/useGrantIntervatAccessToWorkspace';
import { getBEFinSessionQueryKey } from './getBEFinSessionQueryKey';
import { logAmplitude } from 'utils/amplitude';
import { WEBAPP_DOCUMENTSREPOSITORY_CLICKEDCONNECMINFIN } from 'utils/amplitude/events/repository.amplitude';
import { useIsBEFinFeatureEnabled } from 'utils/docsRepo/useIsBEFinFeatureEnabled/useIsBEFinFeatureEnabled';
import { ONE_MINUTE } from 'utils/time';
import showToast from 'utils/toast';

const useBEFinSession = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const intervatGrantHasRun = useRef(false);

  const isFeatureEnabled = useIsBEFinFeatureEnabled();

  const { data, isLoading, refetch } = useQuery(
    getBEFinSessionQueryKey(),
    getBEFinSession,
    {
      staleTime: ONE_MINUTE * 5,
      gcTime: ONE_MINUTE * 30,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      enabled: isFeatureEnabled,
    },
  );

  // Refetch session after redirect from MyMinFin and clean URL
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const source = searchParams.get('source');
  const from = searchParams.get('from');
  const workspaceId = searchParams.get('workspaceId');

  const grantIntervatMutation = useGrantIntervatAccessToWorkspace();
  const { mutate: grantIntervatAccess } = grantIntervatMutation;

  // Handle return from BEFin: Intervat grant or MyMinFin (refetch, redirect if only active session is MinFin, clean URL)
  useEffect(() => {
    if (!isFeatureEnabled || !source) return;

    const cleanUrl = (paramsToDelete: string[]) => {
      const params = new URLSearchParams(location.search);
      paramsToDelete.forEach((param) => params.delete(param));
      const newSearch = params.toString();
      navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, {
        replace: true,
      });
    };

    if (source === INTERVAT_GRANT_SOURCE) {
      if (workspaceId) {
        if (intervatGrantHasRun.current) return;
        intervatGrantHasRun.current = true;
        grantIntervatAccess(workspaceId, {
          onSettled: () => cleanUrl(['source', 'workspaceId']),
        });
      } else {
        cleanUrl(['source', 'workspaceId']);
      }
      return;
    }

    if (source === 'myminfin_connect') {
      logAmplitude(WEBAPP_DOCUMENTSREPOSITORY_CLICKEDCONNECMINFIN, {
        source: from || 'other',
      });

      refetch()
        .then((result) => {
          const newData = result.data;
          const hasIntervat = newData?.consents?.intervatApi || false;
          const hasMmf = newData?.consents?.mmfApi || false;

          if (hasMmf && !hasIntervat) {
            showToast(
              {
                variant: 'warning',
                title: t('integration.toast.intervat_withdrawn.title'),
                linkButtons: [
                  {
                    title: t('integration.toast.intervat_withdrawn.cta'),
                    variant: 'primary',
                    onClick: () => {
                      const redirectUrl = `${window.location.origin}${window.location.pathname}?source=myminfin_connect`;
                      loginToBEFin(redirectUrl).then((res) => {
                        if (res.authUri) {
                          window.location.href = res.authUri;
                        }
                      });
                    },
                  },
                ],
              },
              {
                toastId: 'befin-intervat-withdrawn',
                autoClose: 30000,
                style: {
                  width: '35vw',
                  maxWidth: '600px',
                },
              },
            );
          }
        })
        .finally(() => cleanUrl(['source', 'from']));
    }
  }, [
    isFeatureEnabled,
    source,
    from,
    workspaceId,
    location.search,
    location.pathname,
    navigate,
    grantIntervatAccess,
    refetch,
    t,
  ]);

  return {
    sessionData: data,
    hasAnyConsent:
      data?.consents?.mmfApi || data?.consents?.intervatApi || false,
    mmfConsent: data?.consents?.mmfApi || false,
    intervatConsent: data?.consents?.intervatApi || false,
    isManualSyncAvailable: data?.isManualSyncAvailable || false,
    isLoading,
    refetch,
  };
};

export default useBEFinSession;
